import apiCall, { makeUrl } from '../../utils/apiCall';
import { getFriendlyName } from '../../utils/licenseUtils';
import { call, put, takeLatest } from 'redux-saga/effects';

import moment from 'moment';
import { push } from 'connected-react-router';

const delay = time => new Promise(resolve => setTimeout(resolve, time));

function refreshPage () {
  window.location.reload(false);
}

function safe (sagaFn) {
  return function * (args) {
    try {
      const result = yield call(sagaFn, args);
      return result;
    } catch (error) {
      yield put({ type: 'SET_ERROR', error: (error.response && error.response.data.message) || error.message });
      yield put({ type: 'CLEAR_LOADING' });
    }
  };
}

function * tocNotice (showToc) {
  showToc = false;
  yield put({ type: 'SHOW_TOC', showToc: showToc });
}

function * listLicenses () {
  const listLicenseCall = yield apiCall();
  const result = yield listLicenseCall.get(makeUrl('/license/v2/list?all=1'));

  // Save licenses; reducer will compute hasStandalone synchronously
  yield put({ type: 'SAVE_LICENSES', licenses: result.data.licenses });
}

// download standalone installer.
function * downloadStandaloneInstaller ({ callback }) {
  const downloadInstallerCall = yield apiCall();

  // Request JSON (server returns { url } on success or { type: 'error', message } on failure)
  const result = yield downloadInstallerCall.get(makeUrl('/installer/download', true));

  if (!result || !result.data) {
    yield put({ type: 'SET_ERROR', error: 'No response from server.' });
    yield put({ type: 'CLEAR_LOADING' });
    return;
  }

  // Server returns an error shape
  if (result.data.type === 'error' || result.status >= 400) {
    const errMsg = result.data.message || 'Unable to verify installer access.';
    yield put({ type: 'SET_ERROR', error: errMsg });
    yield put({ type: 'CLEAR_LOADING' });
    return;
  }

  const url = result.data.url;
  if (!url) {
    yield put({ type: 'SET_ERROR', error: 'No download URL returned from server.' });
    yield put({ type: 'CLEAR_LOADING' });
    return;
  }

  // Open the installer URL in the same tab (server-hosted redirect target)
  window.open(url, '_self');

  yield put({ type: 'SET_SUCCESS', success: 'The installer download has been started.' });

  if (callback) {
    // call() handles both sync and async callbacks safely
    yield call(callback);
  }
}

function * downloadLicense ({ licenseId, hardwareId, checkoutTimeframe, callback }) {
  const downloadLicenseCall = yield apiCall();
  const result = yield downloadLicenseCall.post(makeUrl('/license/download256'), { 'license-id': licenseId, 'hardware-id': hardwareId, ttl: checkoutTimeframe });
  yield put({ type: 'SET_FILE', file: result.data });
  yield put({ type: 'SET_SUCCESS', success: 'This license has been generated!' });
  if (callback) {
    yield callback();
  }
}

// function * RquestLicense ({ licenseId }) {
//   const requestLicenseCall = yield apiCall();
//   yield requestLicenseCall.post(makeUrl('/license/request'), { 'license-id': licenseId });
//   // yield put({ type: 'SAVE_LICENSES', licenses: result.data.licenses });
//   yield put({ type: 'SET_SUCCESS', success: 'This request was successful.' });
// }

function * labelLicense ({ licenseId, licenseLabel }) {
  yield put({ type: 'SET_LOADING', key: 'label-license-' + licenseId, loading: true });
  const labelLicenseCall = yield apiCall();
  yield labelLicenseCall.patch(makeUrl('/license/label'), { 'license-id': licenseId, 'license-label': licenseLabel });
  yield put({ type: 'SAVE_LABEL', friendlyId: licenseLabel });
  yield put({ type: 'SET_LOADING', key: 'label-license-' + licenseId, loading: false });
}

function * delegateLicense ({ licenseId, email }) {
  const delegateLicenseCall = yield apiCall();
  yield delegateLicenseCall.patch(makeUrl('/license/delegate'), { 'license-id': licenseId, 'user-email': email });
  if (email === '') {
    yield put({ type: 'SET_SUCCESS', success: 'License has been repossessed.' });
  } else {
    yield put({ type: 'SET_SUCCESS', success: 'This license has been delegated.' });
  }
  yield call(delay, 1000);
  yield call(refreshPage);
}

function * revokeLicense ({ licenseId, hardwareId }) {
  const revokeLicenseCall = yield apiCall();
  yield revokeLicenseCall.post(makeUrl('/license/revoke'), { 'license-id': licenseId, 'hardware-id': hardwareId });
  yield put({ type: 'SET_SUCCESS' });
}

// admin sagas

function * listAllLicenses ({ isActive }) {
  const listAllLicensesCall = yield apiCall();

  if (isActive) {
    const result = yield listAllLicensesCall.get(makeUrl('/admin/v2/listAll?active=1'));
    yield put({ type: 'SAVE_ALL_LICENSES', allLicenses: result.data.licenses });
  } else {
    const result = yield listAllLicensesCall.get(makeUrl('/admin/v2/listAll'));
    yield put({ type: 'SAVE_ALL_LICENSES', allLicenses: result.data.licenses });
  }
}

function * downloadLicenseAll ({ licenseId, hardwareId, checkout, callback }) { // Changed property name
  const downloadLicenseCall = yield apiCall();
  const result = yield downloadLicenseCall.post(makeUrl('/admin/downloadAny'), {
    'license-id': licenseId,
    'hardware-id': hardwareId,
    ttl: checkout // Use 'checkout' directly
  });
  yield put({ type: 'SET_FILE', file: result.data });
  yield put({ type: 'SET_SUCCESS', success: 'This license has been generated!' });
  if (callback) {
    yield callback();
  }
}

function * labelLicenseAll ({ licenseId, licenseLabel }) {
  const labelLicenseCall = yield apiCall();
  yield labelLicenseCall.patch(makeUrl('/admin/labelAny'), { 'license-id': licenseId, 'license-label': licenseLabel });
  yield put({ type: 'SAVE_LABEL', friendlyId: licenseLabel });
}

function * listAllUsers () {
  const listAllUsersCall = yield apiCall();
  const result = yield listAllUsersCall.get(makeUrl('/admin/listAllUsers'));
  yield put({ type: 'SAVE_ALL_USERS', allUsers: result.data.users });
}

function * issueNewLicense ({ email, name, expiration, ttl, licenseType, label, isactive }) {
  const issueNewLicense = yield apiCall();
  const payload = {
    email,
    name,
    expiration: moment(expiration).utc().format('YYYY-MM-DD hh:mm:ss'),
    ttl,
    type: licenseType,
    isactive
  };

  // Make label truly optional: omit it from the payload when blank
  if (typeof label === 'string' && label.trim()) {
    payload.label = label.trim();
  }

  const { data: { message: result } } = yield issueNewLicense.post(makeUrl('/admin/create'), payload);

  yield put({ type: 'LICENSE_ISSUED', result });
}

function * editLicense ({ username, name, licenseId, expiration, ttl, licenseType, isactive }) {
  const issueNewLicense = yield apiCall();
  yield issueNewLicense.patch(makeUrl('/admin/update'), {
    name,
    'license-id': licenseId,
    expiration: moment(expiration).format('YYYY-MM-DD'),
    ttl,
    type: licenseType,
    username,
    isactive
  });
  yield put({ type: 'SET_SUCCESS', success: 'This license has been updated.' });
  yield put(push('/all-licenses'));
}

function * delegateLicenseAll ({ licenseId, email }) {
  const delegateLicenseAllCall = yield apiCall();
  yield delegateLicenseAllCall.patch(makeUrl('/admin/delegateAny'), { 'license-id': licenseId, 'user-email': email });
  if (email === '') {
    yield put({ type: 'SET_SUCCESS', success: 'License has been repossessed.' });
  } else {
    yield put({ type: 'SET_SUCCESS', success: 'This license has been delegated.' });
  }
  yield call(delay, 1000);
  yield call(refreshPage);
}

function * revokeLicenseAll ({ licenseId }) {
  const revokeLicenseCall = yield apiCall();
  yield revokeLicenseCall.patch(makeUrl('/admin/revokeAny'), { 'license-id': licenseId });
  yield put({ type: 'SET_SUCCESS', success: 'This license has been revoked.' });
  yield call(delay, 1000);
  yield call(refreshPage);
}

function * promoteToAdmin (username) {
  const promoteToAdminCall = yield apiCall();
  yield promoteToAdminCall.patch(makeUrl('/admin/AddUserToGroup'), username);
  yield put({ type: 'SET_SUCCESS', success: 'This user has been promoted to admin.' });
}

function * demoteToUser (username) {
  const demoteToUserCall = yield apiCall();
  yield demoteToUserCall.patch(makeUrl('/admin/RemoveUserFromGroup'), username);
  yield put({ type: 'SET_SUCCESS', success: 'This user is no longer an admin.' });
}

function * forcePasswordChange (username) {
  const passwordResetCall = yield apiCall();
  yield passwordResetCall.patch(makeUrl('/admin/ResetUserPassword'), username);
  yield put({ type: 'SET_SUCCESS', success: 'This account password has been reset.' });
}

function * resendVerificationEmail (username) {
  const passwordResetCall = yield apiCall();
  yield passwordResetCall.patch(makeUrl('/admin/ResendSignUp'), username);
  yield put({ type: 'SET_SUCCESS', success: 'A verification email has been resent. Pleaes have the user check their email.' });
}

function * enableUser (username) {
  const passwordResetCall = yield apiCall();
  yield passwordResetCall.patch(makeUrl('/admin/EnableUser'), username);
  yield put({ type: 'SET_SUCCESS', success: 'This user has been enabled.' });
  yield call(delay, 1000);
  yield call(refreshPage);
}

function * disableUser (username) {
  const passwordResetCall = yield apiCall();
  yield passwordResetCall.patch(makeUrl('/admin/DisableUser'), username);
  yield put({ type: 'SET_SUCCESS', success: 'This user has been disabled.' });
  yield call(delay, 1000);
  yield call(refreshPage);
}

function * fetchLicenseTypeStats () {
  const licenseTypeStatsCall = yield apiCall();
  const result = yield licenseTypeStatsCall.get(makeUrl('/admin/license-type-stats'));

  // take result object and map the type to a friendly name
  const mappedResult = result.data.map(item => {
    return {
      type: getFriendlyName(item.type),
      count: item.count
    };
  });

  yield put({ type: 'SAVE_LICENSE_TYPE_STATS', licenseTypeStats: mappedResult });
}

function * fetchUserStats () {
  const userStatsCall = yield apiCall();
  const result = yield userStatsCall.get(makeUrl('/admin/user-stats'));
  yield put({ type: 'SAVE_USER_STATS', userStats: result.data });
}

function * openCustomerInStripe (action) {
  const { stripeCustomerId } = action; // Destructure stripeCustomerId from the action
  if (!stripeCustomerId) {
    yield put({ type: 'SET_ERROR', error: 'No customer ID found.' });
    return;
  }
  window.open(`https://manage.stripe.com/customers/${stripeCustomerId}`, '_blank');
}

function * openCustomerInHubspot (action) {
  const { email } = action; // Destructure stripeCustomerId from the action

  if (!email) {
    yield put({ type: 'SET_ERROR', error: 'No email found.' });
    return;
  }
  // encode email
  const encodedCustEmail = encodeURIComponent(email);
  window.open(`https://app.hubspot.com/contacts/9001676/objects/0-1/views/all/list?query=${encodedCustEmail}`, '_blank');
}

function * saveStripeCustomerId (action) {
  const { username, stripeCustomerId } = action;

  if (!username || !stripeCustomerId) {
    yield put({ type: 'SET_ERROR', error: 'Username and Stripe Customer ID are required.' });
    return;
  }

  try {
    const saveStripeCall = yield apiCall();
    yield saveStripeCall.patch(makeUrl('/admin/SaveStripeCustomerId'), { username, stripe_customer_id: stripeCustomerId });
    yield put({ type: 'SET_SUCCESS', success: 'Stripe Customer ID saved successfully.' });

    // Refresh users list to get updated data
    yield put({ type: 'LIST_USERS' });
  } catch (error) {
    yield put({ type: 'SET_ERROR', error: (error.response && error.response.data.message) || 'Failed to save Stripe Customer ID.' });
  }
}

function * unlinkStripeCustomerId (action) {
  const { username } = action;

  if (!username) {
    yield put({ type: 'SET_ERROR', error: 'Username is required.' });
    return;
  }

  try {
    const unlinkStripeCall = yield apiCall();
    yield unlinkStripeCall.patch(makeUrl('/admin/UnlinkStripeCustomerId'), { username });
    yield put({ type: 'SET_SUCCESS', success: 'Stripe Customer ID unlinked successfully.' });

    // Refresh users list to get updated data
    yield put({ type: 'LIST_USERS' });
  } catch (error) {
    yield put({ type: 'SET_ERROR', error: (error.response && error.response.data.message) || 'Failed to unlink Stripe Customer ID.' });
  }
}

function * openCustomerPortal (action) {
// send to /api/billing/portal will return json with url
  const billingPortalCall = yield apiCall();
  const result = yield billingPortalCall.get(makeUrl('/billing/portal'));

  // check if data has error
  if (result.data.error) {
  // default to billing portal
    window.open('https://billing.imaginary-labs.com/p/login/eVa8wx7HGeO4gg0cMM?prefilled_email=' + encodeURIComponent(action.email), '_self');
  } else {
    window.open(result.data.url, '_self');
  }
}

// New saga: request checkout URL for a product, then navigate to it (same flow as openCustomerPortal)
function * openBillingCheckout (action) {
  const product = (action && action.product) || '';

  if (!product) {
    yield put({ type: 'SET_ERROR', error: 'No product specified.' });
    return;
  }

  const billingCall = yield apiCall();
  const resp = yield billingCall.get(makeUrl(`/billing/checkout?product=${encodeURIComponent(product)}`));

  if (!resp || !resp.data) {
    yield put({ type: 'SET_ERROR', error: 'No response from billing.' });
    return;
  }

  // Check if the response contains an error
  if (resp.data.error) {
    const errMsg = resp.data.message || 'Unable to initiate checkout.';
    yield put({ type: 'SET_ERROR', error: errMsg });
    // after message is cleared, redirecto to base URL
    yield call(delay, 4000);
    window.location.href = '/';
    return;
  }

  // Extract the checkout URL from the response
  const url = resp.data.url;
  if (!url) {
    yield put({ type: 'SET_ERROR', error: 'No checkout URL returned from billing.' });
    return;
  }

  // Navigate to the checkout URL in the same tab
  window.open(url, '_self');
}

/**
 * =================================================
 *  NEW PACKAGE TOOL SAGA FUNCTIONS (NO CONSTANTS)
 * =================================================
 */

function * fetchActivePackages () {
  const request = yield apiCall();
  const response = yield request.get(makeUrl('/admin/get-active-packages'));
  if (response.data) {
    // We inline the 'SAVE_ACTIVE_PACKAGES' string literal
    yield put({ type: 'SAVE_ACTIVE_PACKAGES', packages: response.data });
  } else {
    yield put({ type: 'FETCH_ACTIVE_PACKAGES_FAILURE', error: 'No data returned' });
  }
}
function * fetchActiveMacPackages () {
  const request = yield apiCall();
  const response = yield request.get(makeUrl('/carbon_mac_dmg/get-active-packages'));
  if (response.data) {
    yield put({ type: 'SAVE_ACTIVE_MAC_PACKAGES', packages: response.data });
  } else {
    yield put({ type: 'FETCH_ACTIVE_MAC_PACKAGES_FAILURE', error: 'No data returned' });
  }
}

function * fetchPackageUrl (action) {
  const { packageId } = action;
  const request = yield apiCall();
  const response = yield request.get(makeUrl(`/admin/get-package-url/${packageId}`));
  if (response.data) {
    yield put({ type: 'SAVE_PACKAGE_URL', payload: response.data });
  } else {
    yield put({ type: 'FETCH_PACKAGE_URL_FAILURE', error: 'No data returned' });
  }
}

function * updatePackageUrl (action) {
  const { packageId, newPackageUrl } = action;
  const request = yield apiCall();
  const body = { special_package_id: packageId, new_package_url: newPackageUrl };
  const response = yield request.post(makeUrl('/admin/update-package-url'), body);

  if (response.data) {
    yield put({ type: 'UPDATE_PACKAGE_URL_SUCCESS', payload: response.data });
    // Optionally re-fetch the same package
    yield put({ type: 'FETCH_PACKAGE_URL', packageId });
  } else {
    yield put({ type: 'UPDATE_PACKAGE_URL_FAILURE', error: 'No data returned' });
  }
}

function * fetchPluginMessage () {
  const request = yield apiCall();
  const response = yield request.get(makeUrl('/admin/get-plugin-message'));
  if (response.data) {
    yield put({ type: 'SAVE_PLUGIN_MESSAGE', payload: response.data[0] });
  } else {
    yield put({ type: 'FETCH_PLUGIN_MESSAGE_FAILURE', error: 'No data returned' });
  }
}

function * fetchStandaloneMessage () {
  const request = yield apiCall();
  const response = yield request.get(makeUrl('/admin/get-standalone-message'));
  if (response.data) {
    yield put({ type: 'SAVE_STANDALONE_MESSAGE', payload: response.data[0] });
  } else {
    yield put({ type: 'FETCH_STANDALONE_MESSAGE_FAILURE', error: 'No data returned' });
  }
}

function * fetchStandaloneMacMessage () {
  const request = yield apiCall();
  const response = yield request.get(makeUrl('/admin/get-standalone-mac-message'));
  if (response.data) {
    yield put({ type: 'SAVE_STANDALONE_MAC_MESSAGE', payload: response.data[0] });
  } else {
    yield put({ type: 'FETCH_STANDALONE_MAC_MESSAGE_FAILURE', error: 'No data returned' });
  }
}

function * updatePluginMessage (action) {
  const { newVersion, newMessage } = action;
  const request = yield apiCall();
  const body = { currentVersion: newVersion, upgradeMessage: newMessage };
  const response = yield request.post(makeUrl('/admin/update-plugin-message'), body);

  if (response.data) {
    yield put({ type: 'UPDATE_PLUGIN_MESSAGE_SUCCESS', payload: response.data });
    // Optionally re-fetch the plugin message
    yield put({ type: 'FETCH_PLUGIN_MESSAGE' });
  } else {
    yield put({ type: 'UPDATE_PLUGIN_MESSAGE_FAILURE', error: 'No data returned' });
  }
}
function * updateStandaloneMessage (action) {
  const { newVersion, newMessage } = action;
  const request = yield apiCall();
  const body = { currentVersion: newVersion, upgradeMessage: newMessage };
  const response = yield request.post(makeUrl('/admin/update-standalone-message'), body);

  if (response.data) {
    yield put({ type: 'UPDATE_STANDALONE_MESSAGE_SUCCESS', payload: response.data });
    // Optionally re-fetch the standalone message
    yield put({ type: 'FETCH_STANDALONE_MESSAGE' });
  } else {
    yield put({ type: 'UPDATE_STANDALONE_MESSAGE_FAILURE', error: 'No data returned' });
  }
}

function * updateStandaloneMacMessage (action) {
  const { newVersion, newMessage } = action;
  const request = yield apiCall();
  const body = { currentVersion: newVersion, upgradeMessage: newMessage };
  const response = yield request.post(makeUrl('/admin/update-standalone-mac-message'), body);

  if (response.data) {
    yield put({ type: 'UPDATE_STANDALONE_MAC_MESSAGE_SUCCESS', payload: response.data });
    // Optionally re-fetch the standalone mac message
    yield put({ type: 'FETCH_STANDALONE_MAC_MESSAGE' });
  } else {
    yield put({ type: 'UPDATE_STANDALONE_MAC_MESSAGE_FAILURE', error: 'No data returned' });
  }
}

function * getUpvotyToken () {
  const upvotyApi = yield call(apiCall, {});
  const response = yield call([upvotyApi, upvotyApi.get], makeUrl('/upvoty/token'));
  yield put({ type: 'SET_UPVOTY_TOKEN', upvotyToken: response.data.token });
  // yield call(delay, 1000);
}

function * fetchLicenseUsage (action) {
  try {
    // support both shapes: action.params and direct action properties
    const src = (action && (action.params || action)) || {};
    const { from, to, bucket, topN } = src;
    const request = yield apiCall();

    yield put({ type: 'FETCH_LICENSE_USAGE_LOADING' });

    // build an explicit, encoded query string so the XHR contains the params
    const q = {};
    if (from !== undefined && from !== null) q.from = from;
    if (to !== undefined && to !== null) q.to = to;
    if (bucket !== undefined && bucket !== null) q.bucket = bucket;
    if (topN !== undefined && topN !== null) q.topN = topN;
    const qs = new URLSearchParams(q).toString();

    const usageUrl = makeUrl('/admin/active-count') + (qs ? `?${qs}` : '');
    const usersUrl = makeUrl('/admin/user-usage') + (qs ? `?${qs}` : '');

    const usageResp = yield request.get(usageUrl);
    const usersResp = yield request.get(usersUrl);

    const usageData = usageResp && usageResp.data ? usageResp.data : [];
    const usersRaw = usersResp && usersResp.data ? usersResp.data : [];

    // process users: map -> { name, hours, appearances, sub }, sort by hours desc, slice topN
    const users = (usersRaw || [])
      .map(u => ({
        name: u.name || u.sub,
        hours: Number(u.hours),
        appearances: Number(u.appearances),
        sub: u.sub
      }))
      .sort((a, b) => b.hours - a.hours)
      .slice(0, topN || 20);

    // dispatch results to the store
    yield put({ type: 'SAVE_LICENSE_USAGE', usage: usageData });
    yield put({ type: 'SAVE_LICENSE_USERS', users });
    yield put({ type: 'FETCH_LICENSE_USAGE_SUCCESS', payload: { usage: usageData, users } });
  } catch (err) {
    yield put({ type: 'FETCH_LICENSE_USAGE_FAILURE' });
    yield put({
      type: 'SET_ERROR',
      error: (err.response && err.response.data && err.response.data.message) || err.message
    });
    yield put({ type: 'FETCH_LICENSE_USAGE_ERROR' });
  }
}

function * fetchWatchlistUsage (action) {
  try {
    const src = (action && (action.params || action)) || {};
    const { from, to, bucket, topN, subs } = src;
    const request = yield apiCall();

    yield put({ type: 'FETCH_WATCHLIST_USAGE_LOADING' });

    const q = {};
    if (from !== undefined && from !== null) q.from = from;
    if (to !== undefined && to !== null) q.to = to;
    if (bucket !== undefined && bucket !== null) q.bucket = bucket;
    if (topN !== undefined && topN !== null) q.topN = topN;
    if (subs !== undefined && subs !== null) q.subs = subs;
    const qs = new URLSearchParams(q).toString();

    const usageUrl = makeUrl('/admin/active-count') + (qs ? `?${qs}` : '');
    const usersUrl = makeUrl('/admin/user-usage') + (qs ? `?${qs}` : '');

    const usageResp = yield request.get(usageUrl);
    const usersResp = yield request.get(usersUrl);

    const usageData = usageResp && usageResp.data ? usageResp.data : [];
    const usersRaw = usersResp && usersResp.data ? usersResp.data : [];

    const users = (usersRaw || [])
      .map(u => ({
        name: u.name || u.sub,
        hours: Number(u.hours),
        appearances: Number(u.appearances),
        sub: u.sub
      }))
      .sort((a, b) => b.hours - a.hours)
      .slice(0, topN || 200);

    yield put({ type: 'FETCH_WATCHLIST_USAGE_SUCCESS', payload: { usage: usageData, users } });
  } catch (err) {
    yield put({ type: 'FETCH_WATCHLIST_USAGE_FAILURE' });
    yield put({
      type: 'SET_ERROR',
      error: (err.response && err.response.data && err.response.data.message) || err.message
    });
    yield put({ type: 'FETCH_WATCHLIST_USAGE_ERROR' });
  }
}

function * fetchDashboardStats (action) {
  try {
    const src = (action && (action.params || action)) || {};
    const { from, to, bucket } = src;
    const request = yield apiCall();

    yield put({ type: 'FETCH_DASHBOARD_STATS_LOADING' });

    const q = {};
    if (from !== undefined && from !== null) q.from = from;
    if (to !== undefined && to !== null) q.to = to;
    if (bucket !== undefined && bucket !== null) q.bucket = bucket;
    const qs = new URLSearchParams(q).toString();

    const usageUrl = makeUrl('/admin/stats-usage-by-type') + (qs ? `?${qs}` : '');
    const licUrl = makeUrl('/admin/stats-licenses-usage') + (qs ? `?${qs}` : '');
    const typesUrl = makeUrl('/admin/stats-license-types') + (qs ? `?${qs}` : '');

    // perform requests sequentially (guarantees XHRs fire regardless of saga runtime)
    const usageResp = yield call([request, request.get], usageUrl);
    const licResp = yield call([request, request.get], licUrl);
    const typesResp = yield call([request, request.get], typesUrl);

    // normalize usage timeseries: ensure ts is milliseconds and fields are numeric
    const rawUsage = (usageResp && usageResp.data) ? usageResp.data : [];
    const usage = Array.isArray(rawUsage)
      ? rawUsage.map(u => {
        const rawTs = u?.ts ?? u?.date;
        const n = Number(rawTs);
        const ts = Number.isFinite(n) ? (n < 1e12 ? n * 1000 : n) : NaN;
        return {
          ts,
          metric_30m: Number(u?.m30 ?? u?.metric_30m ?? u?.metric30 ?? 0) || 0,
          metric_24h: Number(u?.m24 ?? u?.metric_24h ?? u?.metric24 ?? 0) || 0,
          metric_7d: Number(u?.m7 ?? u?.metric_7d ?? u?.metric7 ?? 0) || 0
        };
      }).filter(d => Number.isFinite(d.ts))
      : [];

    // normalize licenses time-series similarly
    const rawLic = (licResp && licResp.data) ? licResp.data : [];
    const licenses = Array.isArray(rawLic)
      ? rawLic.map(l => {
        const rawTs = l?.ts ?? l?.date;
        const n = Number(rawTs);
        const ts = Number.isFinite(n) ? (n < 1e12 ? n * 1000 : n) : NaN;
        return {
          ts,
          active: Number(l?.active ?? l?.metric_active_lic ?? 0) || 0,
          expired: Number(l?.expired ?? l?.metric_expired_lic ?? 0) || 0
        };
      }).filter(d => Number.isFinite(d.ts))
      : [];

    // Normalize types to { name, value } so frontend PieChart nameKey/dataKey match
    const rawTypes = (typesResp && typesResp.data) ? typesResp.data : [];
    const types = Array.isArray(rawTypes)
      ? rawTypes.map(t => {
        const rawLabel = t?.label ?? t?.name ?? t?.type ?? 'unknown';
        // map to friendly name via licenseUtils; fall back to rawLabel if mapping missing
        const name = getFriendlyName(rawLabel) || rawLabel || 'unknown';
        const value = Number(t?.value ?? t?.count ?? t?.val ?? 0) || 0;
        return { name, value };
      })
      : [];

    yield put({ type: 'SAVE_DASHBOARD_STATS', payload: { usage, licenses, types } });
  } catch (err) {
    yield put({ type: 'FETCH_DASHBOARD_STATS_FAILURE' });
    yield put({ type: 'SET_ERROR', error: (err.response && err.response.data && err.response.data.message) || err.message });
  }
}

function * fetchLicenseTypesSeries (action) {
  try {
    const src = (action && (action.params || action)) || {};
    const { from, to, bucket, at } = src;
    const request = yield apiCall();

    yield put({ type: 'FETCH_LICENSE_TYPES_SERIES_LOADING' });

    const q = {};
    if (from) q.from = from;
    if (to) q.to = to;
    if (bucket) q.bucket = bucket;
    if (at) q.at = at;
    q.mode = 'series';
    const qs = new URLSearchParams(q).toString();
    const url = makeUrl('/admin/stats-license-types') + (qs
      ? `?${qs}`
      : '');

    const resp = yield call([request, request.get], url);
    const raw = (resp && resp.data) ? resp.data : [];

    // group by ts, accumulate values per label and totals per label
    const byTs = new Map();
    const totals = new Map();
    for (const row of raw) {
      const rawTs = row?.ts ?? row?.date;
      const n = Number(rawTs);
      const ts = Number.isFinite(n)
        ? (n < 1e12
            ? n * 1000
            : n)
        : NaN;
      if (!Number.isFinite(ts)) continue;
      const rawLabel = row.label ?? row.name ?? row.type ?? 'unknown';
      // map to friendly name via licenseUtils; fall back to rawLabel if mapping missing
      const friendly = getFriendlyName(rawLabel) || rawLabel || 'unknown';
      const label = friendly;
      const val = Number(row.value ?? row.count ?? 0) || 0;

      if (!byTs.has(ts)) byTs.set(ts, { ts });
      byTs.get(ts)[label] = val;
      totals.set(label, (totals.get(label) || 0) + val);
    }

    // determine labels order by total descending
    const labels = Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(e => e[0]);

    // build series array sorted by ts
    const series = Array.from(byTs.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([, obj]) => {
        // ensure all labels present (0 if missing)
        for (const lbl of labels) {
          if (!(lbl in obj)) obj[lbl] = 0;
        }
        return obj;
      });

    yield put({ type: 'SAVE_LICENSE_TYPES_SERIES', payload: { series, labels } });
  } catch (err) {
    yield put({ type: 'FETCH_LICENSE_TYPES_SERIES_FAILURE' });
    yield put({ type: 'SET_ERROR', error: (err.response && err.response.data && err.response.data.message) || err.message });
  }
}

function * sagas () {
  yield takeLatest('TOC_NOTICE', safe(tocNotice));
  yield takeLatest('LIST_LICENSES', safe(listLicenses));
  yield takeLatest('DOWNLOAD_LICENSE', safe(downloadLicense));
  yield takeLatest('DOWNLOAD_STANDALONE_INSTALLER', safe(downloadStandaloneInstaller));

  // yield takeLatest('REQUEST_LICENSE', RequestLicense);
  yield takeLatest('UPDATE_LABEL', safe(labelLicense));
  yield takeLatest('DELEGATE_LICENSE', safe(delegateLicense));
  yield takeLatest('REVOKE_LICENSE', safe(revokeLicense));
  yield takeLatest('GET_UPVOTY_TOKEN', safe(getUpvotyToken));
  yield takeLatest('OPEN_CUSTOMER_PORTAL', safe(openCustomerPortal));
  // New billing checkout handler for product-based checkout flow
  yield takeLatest('OPEN_BILLING_CHECKOUT', safe(openBillingCheckout));

  // admin sagas here
  yield takeLatest('LIST_ALL_LICENSES', safe(listAllLicenses));
  yield takeLatest('DOWNLOAD_LICENSE_ALL', safe(downloadLicenseAll));
  yield takeLatest('UPDATE_LABEL_ALL', safe(labelLicenseAll));
  yield takeLatest('LIST_USERS', safe(listAllUsers));
  yield takeLatest('ISSUING_LICENSE', safe(issueNewLicense));
  yield takeLatest('EDIT_LICENSE', safe(editLicense));
  yield takeLatest('DELEGATE_LICENSE_ALL', safe(delegateLicenseAll));
  yield takeLatest('REVOKE_LICENSE_ALL', safe(revokeLicenseAll));

  // admin actions here
  yield takeLatest('PROMOTE_TO_ADMIN', safe(promoteToAdmin));
  yield takeLatest('DEMOTE_TO_USER', safe(demoteToUser));
  yield takeLatest('FORCE_PASSWORD_RESET', safe(forcePasswordChange));
  yield takeLatest('RESEND_VERIFICATION_EMAIL', safe(resendVerificationEmail));
  yield takeLatest('ENABLE_USER', safe(enableUser));
  yield takeLatest('DISABLE_USER', safe(disableUser));
  yield takeLatest('OPEN_CUSTOMER_IN_STRIPE', safe(openCustomerInStripe));
  yield takeLatest('OPEN_CUSTOMER_IN_HUBSPOT', safe(openCustomerInHubspot));
  yield takeLatest('SAVE_STRIPE_CUSTOMER_ID', safe(saveStripeCustomerId));
  yield takeLatest('UNLINK_STRIPE_CUSTOMER_ID', safe(unlinkStripeCustomerId));
  yield takeLatest('FETCH_LICENSE_TYPE_STATS', safe(fetchLicenseTypeStats));
  yield takeLatest('FETCH_LICENSE_USAGE', safe(fetchLicenseUsage));
  yield takeLatest('FETCH_WATCHLIST_USAGE', safe(fetchWatchlistUsage));
  yield takeLatest('FETCH_USER_STATS', safe(fetchUserStats));
  yield takeLatest('FETCH_DASHBOARD_STATS', safe(fetchDashboardStats));
  yield takeLatest('FETCH_LICENSE_TYPES_SERIES', safe(fetchLicenseTypesSeries));

  // deployments sagas
  yield takeLatest('FETCH_ACTIVE_PACKAGES', safe(fetchActivePackages));
  yield takeLatest('FETCH_ACTIVE_MAC_PACKAGES', safe(fetchActiveMacPackages));
  yield takeLatest('FETCH_PACKAGE_URL', safe(fetchPackageUrl));
  yield takeLatest('UPDATE_PACKAGE_URL', safe(updatePackageUrl));
  yield takeLatest('FETCH_PLUGIN_MESSAGE', safe(fetchPluginMessage));
  yield takeLatest('UPDATE_PLUGIN_MESSAGE', safe(updatePluginMessage));
  yield takeLatest('FETCH_STANDALONE_MESSAGE', safe(fetchStandaloneMessage));
  yield takeLatest('UPDATE_STANDALONE_MESSAGE', safe(updateStandaloneMessage));
  yield takeLatest('FETCH_STANDALONE_MAC_MESSAGE', safe(fetchStandaloneMacMessage));
  yield takeLatest('UPDATE_STANDALONE_MAC_MESSAGE', safe(updateStandaloneMacMessage));
}

export default sagas;
